import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';
import FlightIcon from '@mui/icons-material/Flight';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import WorkIcon from '@mui/icons-material/Work';
import useScrollToTop from '../../hooks/useScrollToTop';

const Slovenia: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  useScrollToTop();

  const benefits = [
    { icon: <FlightIcon />, text: t('citizenship.slovenia.benefits.travel') },
    { icon: <CheckCircleIcon />, text: t('citizenship.slovenia.benefits.healthcare') },
    { icon: <WorkIcon />, text: t('citizenship.slovenia.benefits.work') },
    { icon: <FamilyRestroomIcon />, text: t('citizenship.slovenia.benefits.family') },
    { icon: <BusinessIcon />, text: t('citizenship.slovenia.benefits.business') },
    { icon: <SchoolIcon />, text: t('citizenship.slovenia.benefits.quality') },
  ];

  const requirements = [
    t('citizenship.slovenia.requirements.investment'),
    t('citizenship.slovenia.requirements.criminal'),
    t('citizenship.slovenia.requirements.financial'),
    t('citizenship.slovenia.requirements.insurance'),
    t('citizenship.slovenia.requirements.passport'),
    t('citizenship.slovenia.requirements.fee'),
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))',
          backgroundColor: '#2196f3',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '40vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" component="h1" gutterBottom>
              {t('citizenship.slovenia.hero.title')}
            </Typography>
            <Typography variant="h5" gutterBottom>
              {t('citizenship.slovenia.hero.subtitle')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Program Overview */}
      <Container sx={{ py: 8 }}>
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} md={8}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography variant="h4" gutterBottom align="center">
                {t('citizenship.slovenia.overview.title')}
              </Typography>
              <Typography variant="body1" paragraph align="center">
                {t('citizenship.slovenia.overview.description1')}
              </Typography>
              <Typography variant="body1" paragraph align="center">
                {t('citizenship.slovenia.overview.description2')}
              </Typography>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            {t('citizenship.slovenia.benefits.title')}
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ color: 'primary.main', mr: 2 }}>
                          {benefit.icon}
                        </Box>
                        <Typography variant="h6" component="h3">
                          {benefit.text}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Requirements Section */}
      <Container sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          {t('citizenship.slovenia.requirements.title')}
        </Typography>
        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Card sx={{ height: '100%', minHeight: '400px' }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {t('citizenship.slovenia.requirements.investmentTitle')}
                  </Typography>
                  <List>
                    {requirements.map((requirement, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={requirement} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Card sx={{ height: '100%', minHeight: '400px' }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {t('citizenship.slovenia.timeline.title')}
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary={t('citizenship.slovenia.timeline.initial.title')}
                        secondary={t('citizenship.slovenia.timeline.initial.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('citizenship.slovenia.timeline.residence.title')}
                        secondary={t('citizenship.slovenia.timeline.residence.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('citizenship.slovenia.timeline.permanent.title')}
                        secondary={t('citizenship.slovenia.timeline.permanent.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('citizenship.slovenia.timeline.citizenship.title')}
                        secondary={t('citizenship.slovenia.timeline.citizenship.description')}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Slovenia; 